import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Box,
  Button
} from '@mui/material';
import { getDeviceThresholds, updateDeviceThresholds, getDefaultThresholds } from '../API/deviceThresholds';

const TemperatureConfigSection = ({ temperatureDevices, onUpdate }) => {
  const [deviceConfigs, setDeviceConfigs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeviceThresholds = async () => {
      try {
        const configs = {};
        for (const device of temperatureDevices) {
          const response = await getDeviceThresholds(device.dev_eui);
          if (response.status === 'success' && response.thresholds.length > 0) {
            // Organiser les seuils par type (yellow/red)
            const thresholds = response.thresholds.reduce((acc, threshold) => {
              acc[threshold.threshold_type] = threshold;
              return acc;
            }, {});
            
            configs[device.dev_eui] = {
              temperatureType: response.thresholds[0].temperature_type || 'ambiante',
              thresholds,
              alertsEmail: response.thresholds[0].alerts_email || true,
              alertsSms: response.thresholds[0].alerts_sms || false
            };
          } else {
            // Valeurs par défaut si pas de configuration
            const defaults = getDefaultThresholds('ambiante');
            configs[device.dev_eui] = {
              temperatureType: 'ambiante',
              thresholds: {
                yellow: { threshold_value: defaults.yellow },
                red: { threshold_value: defaults.red }
              },
              alertsEmail: true,
              alertsSms: false
            };
          }
        }
        setDeviceConfigs(configs);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    if (temperatureDevices.length > 0) {
      fetchDeviceThresholds();
    }
  }, [temperatureDevices]);

  const handleTemperatureTypeChange = (devEui, newType) => {
    const defaults = getDefaultThresholds(newType);
    setDeviceConfigs(prev => ({
      ...prev,
      [devEui]: {
        ...prev[devEui],
        temperatureType: newType,
        thresholds: {
          yellow: { threshold_value: defaults.yellow },
          red: { threshold_value: defaults.red }
        }
      }
    }));
  };

  const handleThresholdChange = (devEui, thresholdType, value) => {
    setDeviceConfigs(prev => ({
      ...prev,
      [devEui]: {
        ...prev[devEui],
        thresholds: {
          ...prev[devEui].thresholds,
          [thresholdType]: {
            ...prev[devEui].thresholds[thresholdType],
            threshold_value: parseFloat(value) || 0
          }
        }
      }
    }));
  };

  const handleSave = async (devEui) => {
    try {
      const config = deviceConfigs[devEui];
      const thresholds = [
        {
          temperature_type: config.temperatureType,
          threshold_type: 'yellow',
          device_type: 'temperature',
          threshold_value: config.thresholds.yellow.threshold_value,
          unit: 'celsius',
          alerts_email: config.alertsEmail,
          alerts_sms: config.alertsSms
        },
        {
          temperature_type: config.temperatureType,
          threshold_type: 'red',
          device_type: 'temperature',
          threshold_value: config.thresholds.red.threshold_value,
          unit: 'celsius',
          alerts_email: config.alertsEmail,
          alerts_sms: config.alertsSms
        }
      ];

      const response = await updateDeviceThresholds(devEui, thresholds);
      if (response.status === 'success') {
        onUpdate?.(`Configuration sauvegardée pour ${devEui}`);
      }
    } catch (error) {
      // Erreur silencieuse
    }
  };

  if (loading) {
    return <Typography>Chargement de la configuration température...</Typography>;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader title="🌡️ Configuration Température" />
      <CardContent>
        {temperatureDevices.map(device => {
          const config = deviceConfigs[device.dev_eui] || {};
          return (
            <Box key={device.dev_eui} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                {device.name} ({device.brand_name})
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Type de température"
                      value={config.temperatureType || 'ambiante'}
                      onChange={(e) => handleTemperatureTypeChange(device.dev_eui, e.target.value)}
                    >
                      <MenuItem value="ambiante">🏠 Température ambiante</MenuItem>
                      <MenuItem value="chambre_froide_positive">❄️ Chambre froide positive (0-8°C)</MenuItem>
                      <MenuItem value="chambre_froide_negative">🧊 Chambre froide négative (&lt;0°C)</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Seuil jaune (°C)"
                    type="number"
                    value={config.thresholds?.yellow?.threshold_value || 0}
                    onChange={(e) => handleThresholdChange(device.dev_eui, 'yellow', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Seuil rouge (°C)"
                    type="number"
                    value={config.thresholds?.red?.threshold_value || 0}
                    onChange={(e) => handleThresholdChange(device.dev_eui, 'red', e.target.value)}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={() => handleSave(device.dev_eui)}
                  sx={{ mr: 2 }}
                >
                  Sauvegarder
                </Button>
              </Box>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default TemperatureConfigSection;
